import classnames from 'classnames'
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import style from './NavDropdown.module.scss'

function usePopoverState(ref: React.RefObject<HTMLDivElement | null>) {
	const [isOpen, setIsOpen] = useState(false)

	useEffect(() => {
		const element = ref.current
		setIsOpen(element?.matches(':popover-open') ?? false)
		if (!element) {
			return
		}
		const handleToggle = (event: ToggleEvent) => {
			setIsOpen(event.newState === 'open')
		}
		element.addEventListener('toggle', handleToggle)
		return () => {
			element.removeEventListener('toggle', handleToggle)
		}
	}, [])

	return isOpen
}

/**
 * Closes the popover whenever the trigger is hidden (e.g. an ancestor
 * switches to `display: none` at a smaller breakpoint). Without this,
 * the portaled popover would remain anchored to a non-existent element
 * and visually float in the top-left corner of the document.
 */
function useCloseOnTriggerHidden(
	triggerRef: React.RefObject<HTMLElement | null>,
	popoverRef: React.RefObject<HTMLElement | null>,
) {
	useEffect(() => {
		const trigger = triggerRef.current
		if (!trigger) {
			return
		}
		const observer = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect
			const popover = popoverRef.current
			if (width === 0 && height === 0 && popover?.matches(':popover-open')) {
				popover.hidePopover()
			}
		})
		observer.observe(trigger)
		return () => {
			observer.disconnect()
		}
	}, [])
}

export type NavDropdownProps = React.PropsWithChildren<{
	content: React.ReactNode
	offset?: number
	align?: 'start' | 'center' | 'end'
	className?: string
}>

export const NavDropdown: React.FC<NavDropdownProps> = ({
	children,
	content,
	offset = 12,
	align = 'start',
	className,
}) => {
	const triggerId = useId()
	const popoverId = useId()
	const triggerRef = useRef<HTMLButtonElement>(null)
	const popoverRef = useRef<HTMLDivElement>(null)
	const anchorName = `--anchor-${triggerId.replace(/:/g, '')}`
	const isOpen = usePopoverState(popoverRef)
	useCloseOnTriggerHidden(triggerRef, popoverRef)
	return (
		<>
			{/* Trigger */}
			<button
				ref={triggerRef}
				type="button"
				// @ts-expect-error React 18 does not ship with types for popover API
				popovertarget={popoverId}
				style={{ anchorName }}
				data-open={isOpen}
				className={classnames(style.trigger, className)}
			>
				{children}
				<i className="material-icons">expand_more</i>
			</button>

			{/* Popover contents */}
			{createPortal(
				<div
					ref={popoverRef}
					id={popoverId}
					popover="auto"
					style={{
						positionAnchor: anchorName,
						// @ts-expect-error css vars are not included in React.CSSProperties
						'--dropdown-offset': `${offset}px`,
					}}
					className={classnames(style.dropdown, {
						[style.alignStart]: align === 'start',
						[style.alignCenter]: align === 'center',
						[style.alignEnd]: align === 'end',
					})}
				>
					{content}
				</div>,
				document.body,
			)}
		</>
	)
}
